import API_BASE from './config';
import React, { useState } from 'react';
import axios from 'axios';

// ── Single Merch Product Card ─────────────────────────────────────────────────
const MerchCard = ({ item, onOrderSuccess }) => {
  const [selectedSize, setSelectedSize] = useState('M');
  const [quantity, setQuantity] = useState(1);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const token = localStorage.getItem('participant_access_token');
  const participantUser = (() => { try { return JSON.parse(localStorage.getItem('participant_user') || 'null'); } catch { return null; } })();

  // Pre-fill if logged in
  const handleOpenForm = () => {
    if (participantUser) {
      setBuyerEmail(participantUser.email || '');
      const name = [participantUser.first_name, participantUser.last_name].filter(Boolean).join(' ');
      setBuyerName(name);
    }
    setShowForm(true);
    setError('');
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const headers = {};
      await axios.post(`${API_BASE}/api/merch/order/`, {
        item_id: item.id,
        quantity,
        size: item.has_sizes ? selectedSize : 'N/A',
        buyer_name: buyerName,
        buyer_email: buyerEmail,
        buyer_phone: buyerPhone,
      }, { headers });
      setSuccess(true);
      setShowForm(false);
      onOrderSuccess?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Order failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white/[0.02] border border-emerald-500/20 rounded-[1.5rem] p-6 flex flex-col items-center justify-center gap-3 text-center min-h-[200px]">
        <div className="w-12 h-12 bg-emerald-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center">
          <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">Order Placed!</p>
          <h4 className="text-sm font-black text-white uppercase">{item.name}</h4>
          <p className="text-gray-500 text-xs mt-1">The organiser will contact you for pickup/delivery.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/[0.07] rounded-[1.5rem] overflow-hidden hover:border-sky-500/20 transition-all duration-300 flex flex-col">
      {/* Product Image */}
      {item.image_url ? (
        <div className="h-44 overflow-hidden">
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
        </div>
      ) : (
        <div className="h-44 bg-white/[0.03] flex items-center justify-center border-b border-white/[0.05]">
          <svg className="w-10 h-10 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
      )}

      <div className="p-5 flex flex-col flex-grow space-y-3">
        {/* Name + Price */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h4 className="text-sm font-black text-white uppercase tracking-tight">{item.name}</h4>
            {item.description && <p className="text-gray-500 text-xs mt-1 line-clamp-2">{item.description}</p>}
          </div>
          <div className="shrink-0 text-right">
            <p className="text-base font-black text-white">{item.price}</p>
            <p className="text-[9px] text-gray-500 uppercase font-bold">BDT</p>
          </div>
        </div>

        {/* Stock status */}
        <div className="flex items-center gap-2">
          {item.is_sold_out ? (
            <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400">Sold Out</span>
          ) : (
            <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
              {item.stock} left
            </span>
          )}
          {item.has_sizes && <span className="text-[9px] text-gray-600 uppercase font-bold tracking-widest">Sizes available</span>}
        </div>

        <div className="flex-grow" />

        {/* Error */}
        {error && <p className="text-red-400 text-xs font-bold">{error}</p>}

        {/* Action */}
        {!item.is_sold_out && (
          <>
            {!showForm ? (
              <button
                onClick={handleOpenForm}
                className="w-full bg-white/[0.05] border border-white/[0.08] hover:bg-sky-500 hover:border-sky-500 hover:text-black text-gray-300 font-black py-3 rounded-xl uppercase text-[10px] tracking-widest transition-all active:scale-95"
              >
                Order Now
              </button>
            ) : (
              <form onSubmit={handleOrder} className="space-y-3 animate-in fade-in duration-200">
                {/* Size picker */}
                {item.has_sizes && (
                  <div>
                    <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1.5 tracking-widest">Size</label>
                    <div className="flex flex-wrap gap-1.5">
                      {SIZES.map(size => (
                        <button key={size} type="button" onClick={() => setSelectedSize(size)}
                          className={`px-2.5 py-1.5 rounded-lg text-[9px] font-black uppercase border transition-all ${selectedSize === size ? 'bg-sky-500 border-sky-500 text-black' : 'bg-black/40 border-white/10 text-gray-400 hover:border-white/20'}`}>
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity */}
                <div>
                  <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1.5 tracking-widest">Quantity</label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg text-white font-bold text-sm hover:bg-white/10 transition-all">−</button>
                    <span className="text-white font-black text-sm w-8 text-center">{quantity}</span>
                    <button type="button" onClick={() => setQuantity(Math.min(item.stock, quantity + 1))}
                      className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg text-white font-bold text-sm hover:bg-white/10 transition-all">+</button>
                    <span className="text-[10px] text-gray-500 ml-1">× {item.price} = <strong className="text-white">{(quantity * parseFloat(item.price)).toFixed(0)} BDT</strong></span>
                  </div>
                </div>

                {/* Buyer info */}
                <input required value={buyerName} onChange={e => setBuyerName(e.target.value)} placeholder="Your name *"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-sky-500 text-xs" />
                <input type="email" required value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)} placeholder="Email *"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-sky-500 text-xs" />
                <input type="tel" value={buyerPhone} onChange={e => setBuyerPhone(e.target.value)} placeholder="Phone"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-sky-500 text-xs" />

                <p className="text-[9px] text-gray-600 leading-relaxed">
                  The organiser will arrange payment and delivery/pickup after placing the order.
                </p>

                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="flex-1 bg-white/5 text-gray-500 font-black py-2.5 rounded-xl uppercase text-[9px] border border-white/5 hover:text-white transition-colors">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading}
                    className="flex-1 bg-sky-500 text-black font-black py-2.5 rounded-xl uppercase text-[9px] tracking-widest hover:bg-sky-400 transition-all disabled:opacity-50">
                    {loading ? '...' : 'Confirm'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── MerchStore — embedded tab on event detail page ────────────────────────────
const MerchStore = ({ items, eventName, onOrderSuccess }) => {
  if (!items || items.length === 0) {
    return (
      <div className="py-16 text-center border border-dashed border-white/[0.06] rounded-3xl">
        <div className="w-12 h-12 bg-white/[0.03] border border-white/[0.07] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
        <p className="text-gray-600 font-black uppercase tracking-[0.4em] text-[10px] mb-1">No Merch Available</p>
        <p className="text-gray-700 text-xs">Check back closer to the event for merchandise.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-2">Official Store</p>
        <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">{eventName} Merch</h2>
        <p className="text-gray-500 text-xs mt-1">Order official event merchandise. Pickup available at the venue.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {items.filter(i => i.is_active).map(item => (
          <MerchCard key={item.id} item={item} onOrderSuccess={onOrderSuccess} />
        ))}
      </div>
    </div>
  );
};

export default MerchStore;